'use client';

import Link from 'next/link';
import type { CTABlock } from '@impact/types';
import { cn } from '@/lib/utils';
import { CTADiagonal } from './cta-variants/Diagonal';
import { CTACenteredCard } from './cta-variants/CenteredCard';

interface CTABlockRendererProps {
  block: CTABlock;
  editing?: boolean;
}

/**
 * Renders a call-to-action section
 */
export function CTABlockRenderer({ block, editing }: CTABlockRendererProps) {
  switch (block.data.variant ?? 'default') {
    case 'diagonal':
      return <CTADiagonal block={block} editing={editing} />;
    case 'centered-card':
      return <CTACenteredCard block={block} editing={editing} />;
    default:
      break;
  }

  const {
    title,
    subtitle,
    buttonText,
    buttonUrl,
    buttonStyle = 'primary',
    alignment = 'center',
    backgroundStyle = 'light',
  } = block.data;

  const alignmentClasses = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end',
  };

  const backgroundClasses = {
    light: 'bg-muted/30',
    dark: 'bg-zinc-900 text-white',
    primary: 'bg-primary text-primary-foreground',
    gradient: 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground',
  };

  const buttonClasses = {
    primary:
      backgroundStyle === 'light'
        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
        : 'bg-white text-zinc-900 hover:bg-white/90',
    secondary:
      backgroundStyle === 'light'
        ? 'bg-zinc-900 text-white hover:bg-zinc-800'
        : 'bg-white/20 text-white hover:bg-white/30',
    outline:
      backgroundStyle === 'light'
        ? 'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground'
        : 'border-2 border-white text-white hover:bg-white hover:text-zinc-900',
  };

  return (
    <div className={cn('my-12 py-12 px-8 rounded-2xl', backgroundClasses[backgroundStyle])}>
      <div className={cn('flex flex-col gap-4 max-w-2xl mx-auto', alignmentClasses[alignment])}>
        <h2
          className="text-2xl md:text-3xl font-bold"
          {...(editing ? { 'data-editable': 'data.title' } : {})}
        >
          {title}
        </h2>
        {subtitle && (
          <p
            className={cn(
              'text-lg',
              backgroundStyle === 'light' ? 'text-muted-foreground' : 'opacity-90',
            )}
            {...(editing ? { 'data-editable': 'data.subtitle' } : {})}
          >
            {subtitle}
          </p>
        )}
        <Link
          href={editing ? '#' : buttonUrl}
          onClick={editing ? (e) => e.preventDefault() : undefined}
          className={cn(
            'mt-2 px-8 py-3 rounded-lg font-medium transition-colors inline-block',
            buttonClasses[buttonStyle],
          )}
        >
          <span {...(editing ? { 'data-editable': 'data.buttonText' } : {})}>{buttonText}</span>
        </Link>
      </div>
    </div>
  );
}
